import React, { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  IconDashboard,
  IconUsers,
  IconShoppingCart,
  IconChartBar,
  IconReceipt,
  IconSettings,
  IconLogout,
  IconMenu2,
  IconX,
  IconBell,
  IconSearch,
  IconUser,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconChevronDown,
  IconCash,
  IconAd,
  IconActivity
} from '@tabler/icons-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import useAuth from '../../hooks/useAuth'
import { useTheme } from '../../hooks/useTheme'
import ThemeToggle from '../ui/ThemeToggle'
import logger from '../../utils/logger'
import apiService from '../../services/api'

const ModernLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [topbarVisible, setTopbarVisible] = useState(false) // Estado para controlar la visibilidad de la topbar
  const [showFloatingButton, setShowFloatingButton] = useState(true) // Estado para controlar la visibilidad del botón flotante
  const location = useLocation()
  const navigate = useNavigate()
  const { logout, user } = useAuth()
  const { isDark } = useTheme()

  // Query para obtener contador de alertas
  const { data: alertsCount } = useQuery({
    queryKey: ['alerts-count'],
    queryFn: async () => {
      try {
        const response = await apiService.getAlertsCount()
        return response.data || { total: 0 }
      } catch (error) {
        logger.error('Error al obtener contador de alertas:', error)
        return { total: 0 }
      }
    },
    // Removido refetchInterval - se actualizará con la configuración global (10min)
    staleTime: 5 * 60 * 1000 // 5 minutos - las alertas no cambian tan rápido
  })

  // Efecto para detectar el scroll y mostrar/ocultar el botón flotante
  React.useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      setShowFloatingButton(scrollTop < 100) // Mostrar solo cuando estemos en los primeros 100px
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Definir las rutas disponibles para cada rol
  const getNavigationByRole = (role) => {
    const allRoutes = [
      { name: 'Dashboard', href: '/dashboard', icon: IconDashboard },
      { name: 'Vista Gerencial', href: '/executive', icon: IconActivity },
      { name: 'Pedidos', href: '/pedidos', icon: IconShoppingCart },
      { name: 'Reportes', href: '/reportes', icon: IconChartBar },
      { name: 'Facturación', href: '/facturas', icon: IconReceipt },
      { name: 'Ads', href: '/ads', icon: IconAd },
      { name: 'Configuración', href: '/configuracion', icon: IconSettings },
    ]

    switch (role) {
      case 'admin':
      case 'viewer': // Mapear viewer a admin para compatibilidad
        // ADMIN: Acceso completo a todas las funcionalidades
        return allRoutes

      case 'trabajador':
        // TRABAJADOR: Dashboard, Vista Gerencial, Pedidos, Reportes, Ads
        return [
          { name: 'Dashboard', href: '/dashboard', icon: IconDashboard },
          { name: 'Vista Gerencial', href: '/executive', icon: IconActivity },
          { name: 'Pedidos', href: '/pedidos', icon: IconShoppingCart },
          { name: 'Reportes', href: '/reportes', icon: IconChartBar },
          { name: 'Ads', href: '/ads', icon: IconAd },
        ]
      
      case 'cliente':
        // CLIENTE: Dashboard, Pedidos, Reportes, Ads
        return [
          { name: 'Dashboard', href: '/dashboard', icon: IconDashboard },
          { name: 'Pedidos', href: '/pedidos', icon: IconShoppingCart },
          { name: 'Reportes', href: '/reportes', icon: IconChartBar },
          { name: 'Ads', href: '/ads', icon: IconAd },
        ]
      
      case 'dto_cuentas':
        // DEPARTAMENTO DE CUENTAS: Dashboard, Pedidos, Reportes, Facturación, Configuración
        return [
          { name: 'Dashboard', href: '/dashboard', icon: IconDashboard },
          { name: 'Pedidos', href: '/pedidos', icon: IconShoppingCart },
          { name: 'Reportes', href: '/reportes', icon: IconChartBar },
          { name: 'Facturación', href: '/facturas', icon: IconReceipt },
          { name: 'Ads', href: '/ads', icon: IconAd },
          { name: 'Configuración', href: '/configuracion', icon: IconSettings },
        ]
      
      default:
        // Por defecto, solo Dashboard
        return [
          { name: 'Dashboard', href: '/dashboard', icon: IconDashboard },
        ]
    }
  }

  // Obtener las rutas según el rol del usuario
  const userRole = user?.role || 'cliente'
  const navigation = getNavigationByRole(userRole)
  
  // Verificar si es usuario cliente para mostrar información de restricciones
  const isClientUser = userRole === 'cliente'
  const userCompanyName = user?.company_name || 'tu empresa'
  
  // Debug: Log del rol del usuario
  logger.debug('User object:', user)
  logger.debug('User role:', userRole)
  logger.debug('Available navigation:', navigation.map(item => item.name))
  logger.debug('Navigation length:', navigation.length)
  logger.debug('Is client user:', isClientUser)
  logger.debug('User company:', userCompanyName)
  
  // Verificar si el usuario está cargando
  if (!user && userRole === 'cliente') {
    logger.debug('User is loading or not available, using default role: cliente')
  }
  
  // Verificar si el rol es válido
  if (userRole && userRole !== 'cliente') {
    logger.debug('User role detected:', userRole)
  }
  
  // Verificar si la navegación está correcta
  logger.debug('Navigation for role', userRole, ':', navigation.map(item => item.name).join(', '))
  
  // Verificar si hay algún problema con la navegación
  if (navigation.length === 0) {
    logger.error('No navigation items found for role:', userRole)
  }
  
  // Verificar si la navegación tiene solo Dashboard
  if (navigation.length === 1 && navigation[0].name === 'Dashboard') {
    logger.warn('Only Dashboard available for role:', userRole)
  }

  const isActive = (href) => location.pathname === href

  const handleLogout = () => {
    logout()
  }

  return (
    <div
      className="min-h-screen"
      style={{
        background: isDark
          ? 'linear-gradient(to bottom right, #111827, #1f2937, #374151)'
          : 'linear-gradient(to bottom right, #f8fafc, #eff6ff, #eef2ff)'
      }}
    >
      {/* Modern floating background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-4 -right-4 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-600/20 dark:from-blue-600/10 dark:to-purple-800/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-1/4 -left-4 w-96 h-96 bg-gradient-to-br from-indigo-400/15 to-blue-600/15 dark:from-indigo-600/8 dark:to-blue-800/8 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-br from-purple-400/10 to-pink-600/10 dark:from-purple-800/5 dark:to-pink-900/5 rounded-full blur-3xl animate-float" style={{animationDelay: '4s'}}></div>
      </div>

      {/* Modern banner informativo para usuarios cliente */}
      {isClientUser && (
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative backdrop-blur-md border-b border-blue-200/50 px-6 py-4"
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.1))',
            boxShadow: '0 4px 20px 0 rgba(31, 38, 135, 0.15)'
          }}
        >
          <div className="flex items-center justify-center text-sm text-blue-800 font-semibold">
            <IconUser className="h-5 w-5 mr-3 text-blue-600" />
            <span>
              <strong className="text-blue-900">Modo Cliente:</strong> Solo puedes ver datos de {userCompanyName}. 
              Para acceder a todas las empresas, contacta con un administrador.
            </span>
          </div>
        </motion.div>
      )}
      
      {/* Sidebar para móvil */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative flex w-full max-w-xs flex-1 flex-col"
              style={{ background: '#000234' }}
            >
              <div className="flex h-16 items-center justify-between px-3">
                <div className="flex items-center">
                  <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center overflow-hidden">
                    <img 
                      src="/icono_zentrik_tp.png" 
                      alt="Zentrik Logo" 
                      className="h-6 w-6 object-contain"
                    />
                  </div>
                  <img 
                    src="/logo_footer.svg" 
                    alt="Zentrik Logo" 
                    className="ml-2 h-6 w-auto object-contain"
                  />
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="rounded-md p-2 text-gray-300 hover:text-white hover:bg-white/10"
                >
                  <IconX className="h-6 w-6" />
                </button>
              </div>
              <nav className="flex-1 space-y-1 px-3 py-4">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`nav-link ${
                      isActive(item.href) ? 'nav-link-active' : 'nav-link-inactive'
                    }`}
                  >
                    <item.icon className="mr-3 h-5 w-5" />
                    {item.name}
                  </Link>
                ))}
              </nav>
              <div className="border-t border-white/20 p-3">
                <button 
                  onClick={handleLogout}
                  className="flex w-full items-center px-2 py-2 text-sm font-medium text-gray-300 rounded-lg hover:bg-white/10 hover:text-white transition-all duration-200"
                >
                  <IconLogout className="mr-3 h-5 w-5" />
                  Cerrar sesión
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modern Sidebar para desktop */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:flex lg:flex-col transition-all duration-500 z-50 ${
        sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'
      }`}>
        <div className={`flex flex-col flex-grow backdrop-blur-xl border-r border-white/10 ${
          sidebarCollapsed ? 'sidebar-collapsed' : ''
        }`}
        style={{
          background: '#000234',
          boxShadow: '4px 0 24px rgba(0, 0, 0, 0.1)'
        }}>
          <div className="flex h-20 items-center px-4 justify-between">
            <motion.div 
              className="flex items-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="h-10 w-10 rounded-2xl flex items-center justify-center shadow-lg overflow-hidden" style={{ backgroundColor: '#e55a29' }}>
                <img
                  src="/icono_zentrik_tp.png"
                  alt="Zentrik Logo"
                  className="h-8 w-8 object-contain"
                />
              </div>
              {!sidebarCollapsed && (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="ml-3"
                >
                  <img 
                    src="/logo_footer.svg" 
                    alt="Zentrik Logo" 
                    className="h-8 w-auto object-contain"
                  />
                </motion.div>
              )}
            </motion.div>
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-xl hover:bg-white/10 transition-all duration-300 transform hover:scale-110"
              title={sidebarCollapsed ? "Expandir menú" : "Minimizar menú"}
            >
              {sidebarCollapsed ? (
                <IconChevronRight className="h-5 w-5 text-white" />
              ) : (
                <IconChevronLeft className="h-5 w-5 text-white" />
              )}
            </button>
          </div>
          <nav className="flex-1 space-y-2 px-4 py-6">
            {navigation.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link
                  to={item.href}
                  className={`nav-link ${sidebarCollapsed ? 'justify-center' : ''} ${
                    isActive(item.href) ? 'nav-link-active' : 'nav-link-inactive'
                  }`}
                >
                  <item.icon className="h-6 w-6 flex-shrink-0" />
                  {!sidebarCollapsed && (
                    <motion.span 
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: 0.1 }}
                      className="ml-3"
                    >
                      {item.name}
                    </motion.span>
                  )}
                </Link>
              </motion.div>
            ))}
          </nav>
          <div className="border-t border-white/10 p-4">
            <button 
              onClick={handleLogout}
              className={`flex w-full items-center px-4 py-3 text-sm font-semibold text-white rounded-2xl hover:bg-white/10 hover:scale-105 transition-all duration-300 ${
                sidebarCollapsed ? 'justify-center' : ''
              }`}
              style={{
                background: 'linear-gradient(135deg, rgb(133, 37, 5),0.2, rgba(220, 38, 38, 0.2))',
                backdropFilter: 'blur(10px)'
              }}
            >
              <IconLogout className="h-6 w-6 flex-shrink-0" />
              {!sidebarCollapsed && (
                <motion.span 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className="ml-3"
                >
                  Cerrar sesión
                </motion.span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Modern Contenido principal */}
      <div className={`transition-all duration-500 relative ${
        sidebarCollapsed ? 'lg:pl-20' : 'lg:pl-64'
      }`}>
        {/* Botón flotante para mostrar/ocultar topbar */}
        <AnimatePresence>
          {showFloatingButton && (
            <motion.button
              initial={{ opacity: 0, y: -20, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.8 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              onClick={() => setTopbarVisible(!topbarVisible)}
              className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 p-3 rounded-full backdrop-blur-md border hover:scale-110 transition-all duration-300 ${
                isDark 
                  ? 'text-gray-300 hover:text-white border-gray-600/50' 
                  : 'text-gray-600 hover:text-gray-900 border-white/20'
              } ${
                topbarVisible 
                  ? (isDark ? 'bg-green-900/20 border-green-500/50' : 'bg-green-100 border-green-300') 
                  : (isDark ? 'bg-blue-900/20 border-blue-500/50' : 'bg-blue-100 border-blue-300')
              }`}
              style={{
                background: topbarVisible 
                  ? (isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.1)')
                  : (isDark ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.1)'),
                boxShadow: 'var(--shadow-elegant)'
              }}
              title={topbarVisible ? 'Ocultar barra superior' : 'Mostrar barra superior'}
            >
              {topbarVisible ? (
                <IconChevronUp className="h-5 w-5" />
              ) : (
                <IconChevronDown className="h-5 w-5" />
              )}
            </motion.button>
          )}
        </AnimatePresence>

        {/* Indicador sutil cuando la topbar está oculta */}
        {!topbarVisible && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed top-0 left-0 right-0 h-1 z-30"
            style={{
              background: isDark
                ? 'linear-gradient(90deg, rgba(59, 130, 246, 0.5), rgba(147, 51, 234, 0.5))'
                : 'linear-gradient(90deg, rgba(59, 130, 246, 0.3), rgba(147, 51, 234, 0.3))'
            }}
          />
        )}

        {/* Modern Header - Colapsable */}
        <AnimatePresence>
          {topbarVisible && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="sticky top-0 z-40 backdrop-blur-xl border-b overflow-hidden"
              style={{
                background: isDark 
                  ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95))'
                  : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.9))',
                borderColor: isDark ? 'rgba(148, 163, 184, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                boxShadow: isDark 
                  ? '0 4px 20px rgba(0, 0, 0, 0.3)'
                  : '0 4px 20px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="flex h-20 items-center justify-between px-6 sm:px-8 lg:px-10">
                <div className="flex items-center">
                  <button
                    onClick={() => setSidebarOpen(true)}
                    className="lg:hidden rounded-2xl p-3 backdrop-blur-md border border-white/20 text-gray-600 hover:text-gray-900 hover:scale-110 transition-all duration-300"
                    style={{
                      background: 'var(--glass-bg)',
                      boxShadow: 'var(--shadow-elegant)'
                    }}
                  >
                    <IconMenu2 className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="flex items-center space-x-6">
                  {/* Theme Toggle */}
                  <ThemeToggle size="md" />

                  {/* Modern Search Bar */}
                  <div className="hidden sm:block">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <IconSearch className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                      </div>
                      <input
                        type="text"
                        placeholder="Buscar..."
                        className="input-search w-80"
                      />
                    </div>
                  </div>

                  {/* Modern Notifications */}
                  <button
                    onClick={() => navigate('/alertas')}
                    className={`relative p-3 backdrop-blur-md border border-white/20 rounded-2xl hover:scale-110 transition-all duration-300 ${
                      isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                    }`}
                    style={{
                      background: 'var(--glass-bg)',
                      boxShadow: 'var(--shadow-elegant)'
                    }}
                    title="Ver alertas"
                  >
                    <IconBell className="h-6 w-6" />
                    {alertsCount?.total > 0 && (
                      <span className="absolute -top-1 -right-1 h-5 w-5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                        {alertsCount.total > 9 ? '9+' : alertsCount.total}
                      </span>
                    )}
                  </button>

                  {/* Modern User Profile */}
                  <div className="flex items-center space-x-4 p-2 rounded-2xl backdrop-blur-md border border-white/20"
                    style={{
                      background: 'var(--glass-bg)',
                      boxShadow: 'var(--shadow-elegant)'
                    }}
                  >
                    <div className="h-10 w-10 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-lg">
                      <IconUser className="h-6 w-6 text-white" />
                    </div>
                    <div className="hidden sm:block">
                      <div className={`text-sm font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {user?.name || user?.display_name || user?.username || 'Usuario'}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {user?.email || 'usuario@zentrik.com'}
                      </div>
                      <div className={`text-xs font-medium ${isDark ? 'text-blue-300' : 'text-blue-600'}`}>
                        {user?.role === 'admin' ? 'Administrador' : 
                         user?.role === 'trabajador' ? 'Trabajador' : 
                         user?.role === 'cliente' ? 'Cliente' : 
                         user?.role === 'viewer' ? 'Visualizador' : 'Usuario'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Modern Contenido de la página */}
        <main className="flex-1 relative">
          <div className={`transition-all duration-300 ${topbarVisible ? 'py-8' : 'py-4'}`}>
            <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                {children}
              </motion.div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default ModernLayout 